import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const TestimonialSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      company: 'Mitchell Designs LLC',
      text: 'The team at Orbit Marks made the entire trademark registration process seamless. Their expertise and guidance were invaluable for protecting our brand.',
      rating: 5,
    },
    {
      name: 'James Rodriguez',
      company: 'Tech Innovate Inc.',
      text: 'Outstanding service! They handled everything professionally and kept me informed throughout the entire process. Highly recommend for any business.',
      rating: 5,
    },
    {
      name: 'Emily Chen',
      company: 'Artisan Coffee Co.',
      text: 'I was impressed by their attention to detail and quick turnaround time. My trademark was registered without any issues thanks to their thorough approach.',
      rating: 5,
    },
    {
      name: 'Michael Thompson',
      company: 'Urban Fashion Brand',
      text: 'Professional, efficient, and knowledgeable. They answered all my questions and made a complex process easy to understand. Worth every penny!',
      rating: 5,
    },
    {
      name: 'Lisa Anderson',
      company: 'Green Earth Products',
      text: 'Excellent experience from start to finish. The dedicated case manager was always available and helped secure our trademark quickly and affordably.',
      rating: 5,
    },
    {
      name: 'David Park',
      company: 'Park Consulting Group',
      text: 'Exceptional service and incredible attention to detail. They made trademark registration easy and stress-free. Highly professional team!',
      rating: 5,
    },
    {
      name: 'Rachel Williams',
      company: 'Williams Beauty Co.',
      text: 'I cannot recommend them enough! The process was smooth, the communication was clear, and my trademark was secured quickly. Outstanding work!',
      rating: 5,
    },
    {
      name: 'Tom Jackson',
      company: 'Jackson Digital Solutions',
      text: 'From start to finish, the experience was fantastic. They answered every question and made sure my brand was fully protected. Five stars!',
      rating: 5,
    },
    {
      name: 'Jennifer Martinez',
      company: 'Martinez Marketing Agency',
      text: 'Very professional and knowledgeable team. They walked me through every step and ensured my trademark was registered without any complications.',
      rating: 5,
    },
    {
      name: 'Robert Taylor',
      company: 'Taylor Enterprises',
      text: 'Best trademark service I have worked with! Fast, efficient, and highly professional. They protected my brand and gave me peace of mind.',
      rating: 5,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      nextTestimonial();
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const prevTestimonial = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  // Get the three testimonials to display
  const getVisibleTestimonials = () => {
    const prev = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const next = (currentIndex + 1) % testimonials.length;
    return [prev, currentIndex, next];
  };

  const visibleIndices = getVisibleTestimonials();

  return (
    <div className="relative max-w-7xl mx-auto px-4" data-testid="testimonial-slider">
      {/* Desktop View - 3 testimonials at once */}
      <div className="hidden md:block">
        <div className="grid grid-cols-3 gap-6 items-center">
          {visibleIndices.map((index, position) => {
            const isCenter = position === 1;
            return (
              <div
                key={index}
                className={`bg-white rounded-2xl shadow-xl p-8 transition-all duration-500 ${
                  isCenter 
                    ? 'opacity-100 scale-100 z-10' 
                    : 'opacity-40 scale-95'
                }`}
              >
                <div className="flex mb-4">
                  {[...Array(testimonials[index].rating)].map((_, i) => (
                    <Star key={i} className="text-amber-500 fill-current" size={20} />
                  ))}
                </div>
                
                <p className="text-base text-gray-700 mb-6 italic line-clamp-4">
                  "{testimonials[index].text}"
                </p>
                
                <div>
                  <p className="font-bold text-base" style={{ color: 'var(--text-dark)' }}>
                    {testimonials[index].name}
                  </p>
                  <p className="text-sm text-gray-600">{testimonials[index].company}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation arrows - positioned outside */}
        <button
          onClick={prevTestimonial}
          disabled={isTransitioning}
          className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-4 shadow-lg hover:bg-indigo-50 hover:scale-110 transition-all disabled:opacity-50"
          data-testid="testimonial-prev-button"
        >
          <ChevronLeft className="text-indigo-600" size={28} />
        </button>
        <button
          onClick={nextTestimonial}
          disabled={isTransitioning}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-4 shadow-lg hover:bg-indigo-50 hover:scale-110 transition-all disabled:opacity-50"
          data-testid="testimonial-next-button"
        >
          <ChevronRight className="text-indigo-600" size={28} />
        </button>
      </div>

      {/* Mobile View - Single testimonial */}
      <div className="md:hidden">
        <div className="bg-white rounded-2xl shadow-xl p-8 transition-all duration-500">
          <div className="flex mb-4">
            {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
              <Star key={i} className="text-amber-500 fill-current" size={24} />
            ))}
          </div>
          
          <p className="text-lg text-gray-700 mb-6 italic">
            "{testimonials[currentIndex].text}"
          </p>
          
          <div>
            <p className="font-bold text-lg" style={{ color: 'var(--text-dark)' }}>
              {testimonials[currentIndex].name}
            </p>
            <p className="text-gray-600">{testimonials[currentIndex].company}</p>
          </div>
        </div>

        {/* Mobile arrows */}
        <div className="flex justify-center mt-6 space-x-4">
          <button
            onClick={prevTestimonial}
            disabled={isTransitioning}
            className="bg-white rounded-full p-3 shadow-lg hover:bg-indigo-50 transition-all disabled:opacity-50"
          >
            <ChevronLeft className="text-indigo-600" size={24} />
          </button>
          <button
            onClick={nextTestimonial}
            disabled={isTransitioning}
            className="bg-white rounded-full p-3 shadow-lg hover:bg-indigo-50 transition-all disabled:opacity-50"
          >
            <ChevronRight className="text-indigo-600" size={24} />
          </button>
        </div>
      </div>

      {/* Navigation dots */}
      <div className="flex justify-center mt-8 space-x-2">
        {testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              if (!isTransitioning) {
                setIsTransitioning(true);
                setCurrentIndex(index);
                setTimeout(() => setIsTransitioning(false), 500);
              }
            }}
            className={`h-2 rounded-full transition-all ${
              index === currentIndex ? 'bg-indigo-600 w-8' : 'bg-gray-300 w-2'
            }`}
            data-testid={`testimonial-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialSlider;